import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import FeatureTrialModal from "../components/Common/FeatureTrialModal"
import { getFeatureTrials, getMyPlanFeatures, getPlanFeatureCatalog, startFeatureTrial } from "../api/requests/subscription-requests"
import { useAuth } from "../hooks/useAuth"

/** Deneme süresi backend'den gelene kadar gösterilecek değer. */
const DEFAULT_TRIAL_DAYS = 5

/**
 * Provider dışında kalan bileşenler için güvenli varsayılan: hiçbir şey kısıtlanmaz.
 * Özellik listesi olmadan menüyü/sayfayı kapatmak kullanıcıyı sebepsiz durdururdu;
 * asıl kısıt zaten backend'de (403).
 */
const FALLBACK = {
  features: [],
  loaded: false,
  has: () => true,
  trials: [],
  trialDays: DEFAULT_TRIAL_DAYS,
  trialFor: () => null,
  trialableFor: () => undefined,
  startTrial: async () => {},
  canManagePlan: true,
  refresh: async () => {},
}

const PlanFeatureContext = createContext(FALLBACK)

/**
 * Firmanın kullanabildiği özellikler + özellik deneme durumu; panelin tamamına
 * tek kaynaktan servis edilir.
 *
 * Context olmasının sebebi paylaşım: aynı bilgiyi sidebar (menüyü çizmek),
 * sayfa üstündeki deneme sayacı ve "Aboneliğim > Kullanım Hakları" birlikte
 * kullanıyor. Her biri kendi isteğini atsaydı aynı iki uç sayfa başına
 * defalarca çağrılır, üstelik deneme başlatıldığında yalnız biri tazelenirdi.
 *
 * **Güvenlik sınırı değildir** — asıl reddetme `PlanFeatureInterceptor` (403).
 */
export const PlanFeatureProvider = ({ children }) => {
  const [features, setFeatures] = useState([])
  const [loaded, setLoaded] = useState(false)
  const [trials, setTrials] = useState([])
  const [trialDays, setTrialDays] = useState(DEFAULT_TRIAL_DAYS)
  // Kod → denenebilir mi. Katalog durağan olduğu için yalnız bir kez çekilir
  // (özellik/deneme listeleri her sayfa değişiminde tazelenir, bu tazelenmez).
  const [trialableMap, setTrialableMap] = useState(null)
  // Paket kaynaklı 403 yakalandığında açılacak pencerenin hedefi.
  const [deniedFeature, setDeniedFeature] = useState(null)
  // Deneme başlatma ve paket yükseltme yalnız süper admin ile firma admininde (backend 403).
  const { roles } = useAuth()
  const canManagePlan = roles.includes("ROLE_SUPER_ADMIN") || roles.includes("ROLE_COMPANY_ADMIN")

  const load = useCallback(async () => {
    // İki istek birbirinden bağımsız: deneme bilgisi alınamazsa özellik listesi
    // yine de çizilmeli (pencerede yalnız "paketi yükselt" seçeneği kalır).
    const [featureRes, trialRes] = await Promise.allSettled([
      getMyPlanFeatures(),
      getFeatureTrials(),
    ])

    if (featureRes.status === "fulfilled") {
      // Yalnızca istek gerçekten başarılıysa "yüklendi" say. Hata durumunda
      // loaded false kalır ve has() true döner; aksi hâlde bir ağ hatası
      // kullanıcının tüm menülerini uyarı penceresine bağlardı.
      setFeatures(featureRes.value?.data || [])
      setLoaded(true)
    } else {
      setFeatures([])
    }

    if (trialRes.status === "fulfilled") {
      setTrials(trialRes.value?.data?.trials || [])
      setTrialDays(trialRes.value?.data?.trialDays || DEFAULT_TRIAL_DAYS)
    }
  }, [])

  // Sayfa değişiminde yeniden sorgulanır (SubscriptionGate'in abonelik durumunu
  // tazelemesiyle aynı desen): süper admin paketin özelliklerini değiştirdiğinde
  // ya da bir özellik firmadan alındığında panel, kullanıcı tam sayfa yenileme
  // yapmadan güncel kümeyi görsün. Aksi hâlde menü ve deneme sayacı, oturum
  // boyunca ilk yüklenen listede takılı kalırdı.
  // Katalog yalnız yönetici rollerine açık; diğer rollerde istek reddedilir ve
  // harita boş kalır — o durumda pencere kendi yedek listesine düşer. Bu roller
  // zaten deneme başlatamadığı için davranış değişmez.
  useEffect(() => {
    let cancelled = false
    getPlanFeatureCatalog()
      .then((res) => {
        if (cancelled) return
        const map = {}
        for (const f of res?.data || []) {
          if (f?.code) map[f.code] = f.trialable === true
        }
        setTrialableMap(map)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    load().catch(() => setFeatures([]))
  }, [load, location.pathname])

  // Menüde karşılığı olmayan aksiyonlar (SMS gönderimi, Excel aktarımı, iade...)
  // paket yüzünden reddedildiğinde de aynı pencere açılsın diye: axios yakalayıcısı
  // (ContractInterceptor) 403 gövdesindeki featureCode'u olay olarak yayınlar.
  useEffect(() => {
    const onDenied = (e) => {
      const { featureCode, featureLabel, trialable } = e.detail || {}
      if (featureCode) setDeniedFeature({ code: featureCode, label: featureLabel || featureCode, trialable })
    }
    window.addEventListener("plan-feature-denied", onDenied)
    return () => window.removeEventListener("plan-feature-denied", onDenied)
  }, [])

  const value = useMemo(() => ({
    features,
    loaded,
    trials,
    trialDays,

    /** Özellik kullanılabilir mi (pakette VEYA süren deneme). Liste yüklenmediyse kısıtlanmaz. */
    has: (code) => {
      if (!loaded) return true
      if (!code) return true
      return features.includes(code)
    },

    /** Özelliğin deneme kaydı — yoksa null. `active=false` ise hak kullanılmış demektir. */
    trialFor: (code) => trials.find(t => t.code === code) || null,

    /** Denemeyi başlatır ve listeyi yeniler; özellik anında açılır. */
    startTrial: async (code) => {
      const result = await startFeatureTrial(code)
      await load()
      return result
    },

    /**
     * Özellik ücretsiz denemeye uygun mu. Katalog yüklenmediyse (veya rol
     * katalogu göremiyorsa) undefined döner; çağıran taraf kendi yedeğine düşer.
     */
    trialableFor: (code) => (trialableMap ? trialableMap[code] : undefined),

    canManagePlan,

    refresh: load,
  }), [features, loaded, trials, trialDays, trialableMap, canManagePlan, load])

  return (
    <PlanFeatureContext.Provider value={value}>
      {children}

      {/* Reddedilen aksiyon için deneme / yükseltme penceresi. Panelin herhangi
          bir yerinden tetiklenebildiği için burada, tek örnek olarak durur. */}
      {deniedFeature && (
        <FeatureTrialModal
          isOpen={true}
          onClose={() => setDeniedFeature(null)}
          feature={deniedFeature}
          trialable={deniedFeature.trialable ?? (trialableMap ? trialableMap[deniedFeature.code] : undefined)}
          trial={trials.find(t => t.code === deniedFeature.code) || null}
          trialDays={trialDays}
          onStartTrial={async () => {
            await value.startTrial(deniedFeature.code)
            setDeniedFeature(null)
          }}
          onUpgrade={() => {
            setDeniedFeature(null)
            navigate("/subscription/plans")
          }}
        />
      )}
    </PlanFeatureContext.Provider>
  )
}

export const usePlanFeatureContext = () => useContext(PlanFeatureContext)

export default PlanFeatureContext
